<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-end justify-center md:items-center"
  >
    <div class="absolute inset-0 bg-black/50" @click="handleClose"></div>
    <div
      class="relative z-50 flex h-[100svh] w-full flex-col overflow-hidden rounded-none bg-white shadow-2xl md:h-auto md:max-h-[90vh] md:w-[860px] md:rounded-2xl lg:w-[960px]"
    >
      <TicketPurchaseHeader :event-name="eventName" @close="handleClose" />

      <TicketPurchaseStepIndicator
        :active-step="activeStep"
        :step-order="displayStepOrder"
        :step-labels="stepLabels"
      />

      <div class="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 md:p-6">
        <TicketPurchaseTicketsStep
          v-if="activeStep === 'tickets'"
          v-model:ticket-counts="ticketCounts"
          :ticket-types="ticketTypes"
          :currency="currency"
        />

        <TicketPurchaseServicesStep
          v-else-if="activeStep === 'services'"
          :ticket-entries="ticketEntries"
          :service-options="serviceOptions"
          @update:ticket-entries="ticketEntries = $event"
          @skip="goNextStep"
        />

        <TicketPurchaseAuthStep
          v-else-if="activeStep === 'auth'"
          @guest="startGuestFlow"
        />

        <TicketPurchaseGuestDetailsStep
          v-else-if="activeStep === 'guest-details'"
          :ticket-entries="ticketEntries"
          :use-same-guest="useSameGuest"
          :age-group-options="ageGroupOptions"
          :accept-terms="acceptTerms"
          :newsletter-consent-seller="newsletterConsentSeller"
          :newsletter-consent-upfest="newsletterConsentUpfest"
          :seller-name="sellerName ?? 'the event organiser'"
          :age-errors="ageErrors"
          @update:ticket-entries="ticketEntries = $event"
          @update:use-same-guest="useSameGuest = $event"
          @update:accept-terms="acceptTerms = $event"
          @update:newsletter-consent-seller="newsletterConsentSeller = $event"
          @update:newsletter-consent-upfest="newsletterConsentUpfest = $event"
        />

        <TicketPurchasePaymentStep
          v-else-if="activeStep === 'payment'"
          :ticket-entries="ticketEntries"
          :selected-tickets-list="selectedTicketsList"
          :service-options="serviceOptions"
          :guest-email="ticketEntries[0]?.guest?.email ?? ''"
          :total-price="totalPrice"
          :currency="currency"
          :checkout-status="checkoutStatus"
          :seller-name="sellerName ?? 'the event organiser'"
          @pay-now="handlePayNow"
        />
      </div>

      <TicketPurchaseFooterActions
        :active-step="activeStep"
        :can-go-back="canGoBack"
        :total-tickets="totalTickets"
        :is-guest-form-valid="isGuestFormValid()"
        :checkout-status="checkoutStatus"
        @back="goPrevStep"
        @next="goNextStep"
        @review="handleGuestReview"
        @pay-now="handlePayNow"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, toRefs } from "vue";
import TicketPurchaseAuthStep from "./ticket-purchase/TicketPurchaseAuthStep.vue";
import TicketPurchaseFooterActions from "./ticket-purchase/TicketPurchaseFooterActions.vue";
import TicketPurchaseGuestDetailsStep from "./ticket-purchase/TicketPurchaseGuestDetailsStep.vue";
import TicketPurchaseHeader from "./ticket-purchase/TicketPurchaseHeader.vue";
import TicketPurchasePaymentStep from "./ticket-purchase/TicketPurchasePaymentStep.vue";
import TicketPurchaseServicesStep from "./ticket-purchase/TicketPurchaseServicesStep.vue";
import TicketPurchaseStepIndicator from "./ticket-purchase/TicketPurchaseStepIndicator.vue";
import TicketPurchaseTicketsStep from "./ticket-purchase/TicketPurchaseTicketsStep.vue";
import type {
  PurchasePayload,
  TicketFlowStep,
  TicketEntry,
} from "./ticket-purchase/types";
import { defaultGuestInfo } from "./ticket-purchase/types";
import {
  clampToAvailability,
  isUnavailable,
} from "./ticket-purchase/availability";

const { t } = useI18n();

type TicketLike = {
  id?: string | number;
  ticketTypeId?: string | number;
  name?: string;
  ticketName?: string;
  price?: number;
  amount?: number;
  currency?: string;
  maxQuantity?: number;
  sold?: number;
  held?: number;
  available?: number | null;
};

type ServiceOptionLike = {
  id?: string | number;
  name?: string;
  price?: number;
  currency?: string;
  maxQuantity?: number;
  sold?: number;
  held?: number;
  available?: number | null;
};

const props = defineProps<{
  show: boolean;
  eventName: string;
  ticketTypes: TicketLike[];
  serviceOptions: ServiceOptionLike[];
  currency: string;
  persistKey?: string;
  sellerName?: string;
  eventId?: string;
  ageRestrictionEnabled?: boolean;
  minimumAge?: number;
}>();

const { show, eventName, ticketTypes, serviceOptions, currency } =
  toRefs(props);

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
  (e: "close"): void;
  (e: "pay" | "login", payload: PurchasePayload): void;
}>();

const activeStep = ref<TicketFlowStep>("tickets");
const stepOrder: TicketFlowStep[] = [
  "tickets",
  "services",
  "auth",
  "guest-details",
  "payment",
];
const stepLabels = computed<Record<TicketFlowStep, string>>(() => ({
  "tickets": t("ticketPurchase.steps.tickets"),
  "services": t("ticketPurchase.steps.services"),
  "auth": t("ticketPurchase.steps.auth"),
  "guest-details": t("ticketPurchase.steps.guestDetails"),
  "payment": t("ticketPurchase.steps.pay"),
}));

const ticketCounts = ref<Record<string, number>>({});
const ticketEntries = ref<TicketEntry[]>([]);
const useSameGuest = ref(true);
const acceptTerms = ref(false);
const newsletterConsentSeller = ref(false);
const newsletterConsentUpfest = ref(false);
const checkoutStatus = ref<string | null>(null);
const ageErrors = ref<Record<number, string>>({});

const ageGroupOptions = computed(() => [
  t("ticketPurchase.guest.ageGroups.0to6"),
  t("ticketPurchase.guest.ageGroups.7to12"),
  t("ticketPurchase.guest.ageGroups.13to16"),
  t("ticketPurchase.guest.ageGroups.17to18"),
  t("ticketPurchase.guest.ageGroups.19to21"),
  t("ticketPurchase.guest.ageGroups.22to30"),
  t("ticketPurchase.guest.ageGroups.31to40"),
  t("ticketPurchase.guest.ageGroups.41to50"),
  t("ticketPurchase.guest.ageGroups.51to60"),
  t("ticketPurchase.guest.ageGroups.60plus"),
]);

const ticketId = (t: TicketLike) =>
  String(t.id ?? t.ticketTypeId ?? t.name ?? "ticket");
const ticketPrice = (t: TicketLike) => {
  const price = Number(t.price ?? t.amount ?? 0);
  return Number.isFinite(price) ? price : Number.POSITIVE_INFINITY;
};

function isSoldOut(ticket: TicketLike): boolean {
  return isUnavailable(ticket);
}

function hasSelectionForCurrentTickets(): boolean {
  return ticketTypes.value.some((ticket) => {
    const qty = ticketCounts.value[ticketId(ticket)] ?? 0;
    return qty > 0;
  });
}

function findCheapestAvailableTicket(types: TicketLike[]): TicketLike | null {
  let cheapest: TicketLike | null = null;
  for (const ticket of types) {
    if (isSoldOut(ticket)) continue;
    if (!cheapest || ticketPrice(ticket) < ticketPrice(cheapest)) {
      cheapest = ticket;
    }
  }
  return cheapest;
}

function ensureDefaultTicketSelection() {
  if (!ticketTypes.value.length) return;
  if (hasSelectionForCurrentTickets()) return;

  const cheapest = findCheapestAvailableTicket(ticketTypes.value);
  if (!cheapest) return;

  ticketCounts.value = {
    ...ticketCounts.value,
    [ticketId(cheapest)]: 1,
  };
}

function clampTicketCountsToAvailability() {
  ticketCounts.value = Object.fromEntries(
    ticketTypes.value.map((ticket) => [
      ticketId(ticket),
      clampToAvailability(ticket, ticketCounts.value[ticketId(ticket)] ?? 0),
    ]),
  );
}

// Reactive age-group validation — show errors as soon as the user picks an age group
watch(
  () => ticketEntries.value.map((e) => e.guest?.ageGroup),
  () => {
    if (
      !props.ageRestrictionEnabled ||
      !props.minimumAge ||
      props.minimumAge <= 0
    ) {
      ageErrors.value = {};
      return;
    }
    const entries = useSameGuest.value
      ? [ticketEntries.value[0]]
      : ticketEntries.value;
    const errors: Record<number, string> = {};
    entries.forEach((entry, idx) => {
      if (!entry?.guest?.ageGroup) return;
      const lower = ageGroupLowerBound(entry.guest.ageGroup);
      if (lower >= 0 && lower < props.minimumAge!) {
        errors[idx] = t("ticketPurchase.guestDetails.ageRestricted", {
          age: props.minimumAge,
        });
      }
    });
    ageErrors.value = errors;
  },
);

const selectedTicketsList = computed(() =>
  ticketTypes.value
    .map((t) => ({
      item: t,
      id: ticketId(t),
      qty: ticketCounts.value[ticketId(t)] ?? 0,
    }))
    .filter((t) => t.qty > 0),
);

const totalTickets = computed(() =>
  selectedTicketsList.value.reduce((acc, t) => acc + t.qty, 0),
);

// Sync ticketEntries whenever ticketCounts OR ticketTypes changes — expand each ticket type × qty.
// ticketTypes must also be a dependency: if it is empty (still loading), skip the rebuild so that
// restored guest data (set by restoreSelection) is not wiped before the prop is populated.
// Preserve existing guest/service data for entries that still exist.
watch(
  [ticketCounts, ticketTypes],
  () => {
    if (!ticketTypes.value.length) return;
    const newEntries: TicketEntry[] = [];
    for (const tt of ticketTypes.value) {
      const id = ticketId(tt);
      const qty = ticketCounts.value[id] ?? 0;
      const name = tt.name ?? tt.ticketName ?? "Ticket";
      for (let i = 0; i < qty; i++) {
        // Try to keep existing entry
        const existing = ticketEntries.value.find(
          (e) => e.ticketTypeId === id && e.ticketIndex === i,
        );
        if (existing) {
          newEntries.push(existing);
        } else {
          newEntries.push({
            ticketTypeId: id,
            ticketTypeName: name,
            ticketIndex: i,
            guest: defaultGuestInfo(),
            serviceCounts: {},
          });
        }
      }
    }
    ticketEntries.value = newEntries;
  },
  { deep: true, immediate: true },
);

const totalPrice = computed(() => {
  let total = 0;
  // Ticket prices
  for (const t of selectedTicketsList.value) {
    total += (Number(t.item.price ?? t.item.amount ?? 0) || 0) * t.qty;
  }
  // Service prices — sum from each ticket entry
  for (const entry of ticketEntries.value) {
    for (const [svcId, qty] of Object.entries(entry.serviceCounts)) {
      const svc = serviceOptions.value.find(
        (s) => String(s.id ?? s.name ?? "service") === svcId,
      );
      if (svc && qty > 0) {
        total += (Number(svc.price ?? 0) || 0) * qty;
      }
    }
  }
  return total;
});

const hasServices = computed(() => serviceOptions.value?.length > 0);
const displayStepOrder = computed(() =>
  hasServices.value ? stepOrder : stepOrder.filter((s) => s !== "services"),
);
const currentStepIndex = computed(() =>
  displayStepOrder.value.indexOf(activeStep.value),
);
const canGoBack = computed(() => currentStepIndex.value > 0);

watch(displayStepOrder, (order) => {
  if (!order.includes(activeStep.value)) {
    activeStep.value = order[0] ?? "tickets";
  }
});

const isGuestFormValid = () => {
  if (!acceptTerms.value) return false;
  if (useSameGuest.value) {
    const g = ticketEntries.value[0]?.guest;
    if (!g) return false;
    return (
      !!g.firstName.trim() &&
      !!g.lastName.trim() &&
      !!g.email.trim() &&
      !!g.ageGroup &&
      !!g.sex
    );
  }
  // All entries must be valid
  return ticketEntries.value.every((entry) => {
    const g = entry.guest;
    return (
      !!g.firstName.trim() &&
      !!g.lastName.trim() &&
      !!g.email.trim() &&
      !!g.ageGroup &&
      !!g.sex
    );
  });
};

function resetState() {
  ticketCounts.value = {};
  ticketEntries.value = [];
  useSameGuest.value = true;
  acceptTerms.value = false;
  newsletterConsentSeller.value = false;
  newsletterConsentUpfest.value = false;
  activeStep.value = "tickets";
  checkoutStatus.value = null;
  if (props.persistKey && import.meta.client) {
    try {
      localStorage.removeItem(props.persistKey);
    } catch {
      /* ignore */
    }
  }
}

const persistSelection = () => {
  if (!props.persistKey || !import.meta.client) return;
  const payload = {
    ticketCounts: { ...ticketCounts.value },
    ticketEntries: ticketEntries.value,
    useSameGuest: useSameGuest.value,
    acceptTerms: acceptTerms.value,
    newsletterConsentSeller: newsletterConsentSeller.value,
    newsletterConsentUpfest: newsletterConsentUpfest.value,
  };
  try {
    localStorage.setItem(props.persistKey, JSON.stringify(payload));
  } catch (error) {
    console.warn("persistSelection failed", error);
  }
};

const restoreSelection = () => {
  if (!props.persistKey || !import.meta.client) return;
  try {
    const raw = localStorage.getItem(props.persistKey);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed.ticketCounts) ticketCounts.value = { ...parsed.ticketCounts };
    if (Array.isArray(parsed.ticketEntries))
      ticketEntries.value = parsed.ticketEntries;
    if (typeof parsed.useSameGuest === "boolean")
      useSameGuest.value = parsed.useSameGuest;
    if (typeof parsed.acceptTerms === "boolean")
      acceptTerms.value = parsed.acceptTerms;
    if (typeof parsed.newsletterConsentSeller === "boolean")
      newsletterConsentSeller.value = parsed.newsletterConsentSeller;
    if (typeof parsed.newsletterConsentUpfest === "boolean")
      newsletterConsentUpfest.value = parsed.newsletterConsentUpfest;
    clampTicketCountsToAvailability();
  } catch (error) {
    console.warn("restoreSelection failed", error);
  }
};

watch(ticketCounts, persistSelection, { deep: true });
watch(ticketEntries, persistSelection, { deep: true });
watch(useSameGuest, persistSelection);
watch(acceptTerms, persistSelection);

// Disable body scroll while modal is open and restore on close/unmount
let previousBodyOverflow = "";
function disableBodyScroll() {
  if (!import.meta.client) return;
  try {
    previousBodyOverflow = document.body.style.overflow || "";
    document.body.style.overflow = "hidden";
  } catch {
    /* ignore */
  }
}

function enableBodyScroll() {
  if (!import.meta.client) return;
  try {
    document.body.style.overflow = previousBodyOverflow || "";
    previousBodyOverflow = "";
  } catch {
    /* ignore */
  }
}

watch(show, (val) => {
  if (!import.meta.client) return;
  if (val) disableBodyScroll();
  else enableBodyScroll();
});

watch(
  [show, ticketTypes],
  ([isVisible]) => {
    if (!isVisible) return;
    clampTicketCountsToAvailability();
    ensureDefaultTicketSelection();
  },
  { immediate: true },
);

watch(
  [ticketEntries, serviceOptions],
  () => {
    const remaining = new Map(
      serviceOptions.value.map((service) => [
        String(service.id ?? service.name ?? "service"),
        clampToAvailability(service, Number.MAX_SAFE_INTEGER),
      ]),
    );
    let changed = false;
    const entries = ticketEntries.value.map((entry) => {
      const serviceCounts = { ...entry.serviceCounts };
      for (const [id, count] of Object.entries(serviceCounts)) {
        const capacity = remaining.get(id);
        if (capacity === undefined) continue;
        const next = Math.min(count, capacity);
        remaining.set(id, capacity - next);
        if (next !== count) {
          serviceCounts[id] = next;
          changed = true;
        }
      }
      return changed ? { ...entry, serviceCounts } : entry;
    });
    if (changed) ticketEntries.value = entries;
  },
  { deep: true },
);

onUnmounted(() => {
  enableBodyScroll();
});

function goToStep(step: TicketFlowStep) {
  if (!displayStepOrder.value.includes(step)) return;
  activeStep.value = step;
  checkoutStatus.value = null;
}

function goNextStep() {
  const order = displayStepOrder.value;
  const idx = order.indexOf(activeStep.value);
  const next = order[idx + 1];
  if (next) goToStep(next);
}

function goPrevStep() {
  const order = displayStepOrder.value;
  const idx = order.indexOf(activeStep.value);
  const prev = order[idx - 1];
  if (prev) goToStep(prev);
}

function startGuestFlow() {
  goToStep("guest-details");
}

function ageGroupLowerBound(ag: string): number {
  ag = ag.toLowerCase().trim();
  if (!ag) return -1;
  if (ag.includes("under") || ag.includes("unter")) return 0;
  ag = ag.replace(/\+$/, "").replace(/plus$/, "");
  const parts = ag.split("-");
  if (parts.length === 1) {
    const enDash = ag.split("\u2013");
    if (enDash.length > 1) return parseInt(enDash[0]!.trim(), 10) || -1;
  }
  const n = parseInt(parts[0]!.trim(), 10);
  return isNaN(n) ? -1 : n;
}

function buildPayload(): PurchasePayload {
  // When useSameGuest, replicate the first guest to all entries
  let entries = ticketEntries.value;
  if (useSameGuest.value && entries.length > 0) {
    const shared = entries[0]!.guest;
    entries = entries.map((e) => ({ ...e, guest: { ...shared } }));
  }

  return {
    tickets: selectedTicketsList.value,
    ticketEntries: entries,
    useSameGuest: useSameGuest.value,
    acceptTerms: acceptTerms.value,
    newsletterConsentSeller: newsletterConsentSeller.value,
    newsletterConsentUpfest: newsletterConsentUpfest.value,
    totalPrice: totalPrice.value,
    step: activeStep.value,
  };
}

async function handleGuestReview() {
  if (!isGuestFormValid()) return;
  ageErrors.value = {};
  checkoutStatus.value = null;

  // Client-side age restriction check
  if (props.ageRestrictionEnabled && props.minimumAge && props.minimumAge > 0) {
    const entries = useSameGuest.value
      ? [ticketEntries.value[0]]
      : ticketEntries.value;
    const errors: Record<number, string> = {};
    entries.forEach((entry, idx) => {
      if (!entry?.guest?.ageGroup) return;
      const lower = ageGroupLowerBound(entry.guest.ageGroup);
      if (lower >= 0 && lower < props.minimumAge!) {
        errors[idx] = t("ticketPurchase.guestDetails.ageRestricted", {
          age: props.minimumAge,
        });
      }
    });
    if (Object.keys(errors).length > 0) {
      ageErrors.value = errors;
      return;
    }
  }

  goToStep("payment");
  persistSelection();
}

function handlePayNow() {
  checkoutStatus.value =
    "Payment initiated. Tickets will be downloaded and a confirmation email will be sent.";
  persistSelection();
  // Clear the modal's own persist key now that the session has been handed off to the
  // checkout page. This prevents stale data being restored if the user navigates back.
  if (props.persistKey && import.meta.client) {
    try {
      localStorage.removeItem(props.persistKey);
    } catch {
      /* ignore */
    }
  }
  emit("pay", buildPayload());
}

function handleClose() {
  resetState();
  emit("update:show", false);
  emit("close");
}

onMounted(() => {
  restoreSelection();
});
</script>

<style scoped>
/* Modal styling mostly via utility classes */
</style>
